/* eslint-disable no-param-reassign */
import cloneDeep from 'lodash/cloneDeep';
import get from 'lodash/get';
import isObject from 'lodash/isObject';

import {
  AggrKeyInterface,
  GroupByInterface,
  TableGroupByInterface,
  ValueFormatter,
  ValueMap,
} from '../../../@types';

import {
  sanitizeKey,
  parseDataValue,
  formatValue,
} from '../../../utils/dataUtils';
import { ReferenceMap } from './reference';
import {
  AggregateObj,
  firstPassAggregateFunction,
  secondPassAggregateFunction,
} from './aggregation';
import { isDataValid } from './filter';

//
// Exported interface
//

export type DataParser = (input: {
  data: Array<Record<string, unknown>>;
  aggrKey: AggrKeyInterface;
  groupBy: Array<GroupByInterface>;
  reference: ReferenceMap;
  subRows?: { aggrKey: AggrKeyInterface };
}) => Array<Record<string, string | number | Record<string, string | number>>>;

interface AggregationMapValue extends AggregateObj {
  formatter?: ValueFormatter;
  mapping?: Array<ValueMap>;
}

type IntermediaryMappedValue = string | number | AggregationMapValue;

type IntermediaryMappedData = Record<string, IntermediaryMappedValue> & {
  _original: string;
  subRows?: Omit<Record<string, IntermediaryMappedData>, 'subRows'>;
};

const getInitialValue = (
  group: GroupByInterface,
  defaultValue: string | number,
): string | number => (group.aggregation ? 0 : defaultValue);

/**
 * Builds a lookup of result-key -> configured `defaultValue` for aggregation
 * columns that define one. Aggregation cells are seeded with `0` (see
 * getInitialValue) and only overwritten for the rows/months that actually have
 * data; the untouched ones stay `0`. This map lets the finalizer surface the
 * column's `defaultValue` (e.g. '-') for those empty cells instead of `0`,
 * while a genuine aggregated `0` still renders as `0` (it goes through the
 * aggregation-object branch). Keys mirror getInnerObjectBuilder so reference
 * (e.g. date-filter) columns are expanded to one entry per reference label.
 */
const buildDefaultValueMap = (
  groupBy: Array<GroupByInterface>,
  referenceMap: ReferenceMap,
): Record<string, string | number> => {
  const map: Record<string, string | number> = {};

  const register = (key: string, group: GroupByInterface) => {
    if (group.aggregation && group.defaultValue !== undefined) {
      map[key] = group.defaultValue;
    }
  };

  groupBy.forEach((g) => {
    const { subGroups } = g as TableGroupByInterface;
    const groupHasSubgroups = subGroups && subGroups.length > 0;

    if (g.reference && referenceMap[g.reference.id]) {
      referenceMap[g.reference.id].forEach((ref) => {
        if (!groupHasSubgroups) register(ref.label, g);
        subGroups?.forEach((sg) => register(`${ref.label}-${sg.key}`, sg));
      });
      return;
    }

    if (!groupHasSubgroups) register(g.key, g);
    subGroups?.forEach((sg) => register(`${g.key}-${sg.key}`, sg));
  });

  return map;
};

type TableData = Record<
  string,
  string | number | Record<string, string | number>
> & { subRows?: TableData[] };

/**
 * Returns a function to create the object structure with default values.
 */
const getInnerObjectBuilder =
  (
    groupBy: Array<GroupByInterface>,
    referenceMap: ReferenceMap,
    isParentRow?: boolean,
  ) =>
  () => {
    const result: IntermediaryMappedData = { _original: '' };

    // Iterate over each group, building the array
    groupBy.forEach((g) => {
      // Hide this column on parent rows
      if (isParentRow && (g as TableGroupByInterface).hideOnParentRow) {
        return;
      }

      // Check for subgroups (Table). If they are present, we need a different
      // approach to iterate over each subgroups
      const { subGroups } = g as TableGroupByInterface;
      const groupHasSubgroups = subGroups && subGroups?.length > 0;

      // If group has a reference
      if (g.reference && referenceMap[g.reference.id]) {
        referenceMap[g.reference.id].forEach((ref) => {
          // For each ref label, add 0 (if aggregation) or label
          if (!groupHasSubgroups) {
            result[ref.label] = getInitialValue(g, ref.label);
          }

          // Iterate over each subgroup (if any)
          // For each ref label, add 0 (if aggregation) or label
          subGroups?.forEach((sg) => {
            const sgKey = `${ref.label}-${sg.key}`;
            result[sgKey] = getInitialValue(sg, sg.defaultValue || sg.label);
          });
        });
        return;
      }

      // For each ref label, add 0 (if aggregation) or label
      if (!groupHasSubgroups) {
        result[g.key] = getInitialValue(g, g.defaultValue || g.label);
      }

      // Iterate over each subgroup
      // For each ref label, add 0 (if aggregation) or label
      subGroups?.forEach((sg) => {
        const sgKey = `${g.key}-${sg.key}`;
        result[sgKey] = getInitialValue(sg, sg.defaultValue || sg.label);
      });
    });

    return result;
  };

/**
 * Performs post-processing aggregation (2nd-pass)
 */
const performSecondDataPass = (
  k: string,
  value: IntermediaryMappedValue,
  entries: IntermediaryMappedData[],
  idx: number,
  defaultValueByKey: Record<string, string | number> = {},
): string | number => {
  const allEntriesForKey = entries
    .filter((e) => !!get(e, k))
    .map((e) => get(e, k));

  // This value is a AggregationMapValue
  if (isObject(value) && k !== '_original') {
    const intermediate = secondPassAggregateFunction(
      value as AggregateObj,
      allEntriesForKey as Array<AggregateObj>,
    );

    /**
     * Apply dynamic formatting based on a type field in the same row.
     * This should probably be moved to formatValue, but it'll take a huge refactoring
     * as we don't have access to the entire row inside this function by now.
     *
     * This was implemented for handling aggregated fields on FacilitiesSurveyHistoryPage.
     */
    let { formatter } = value as AggregationMapValue;
    if (formatter?.type === 'dynamic') {
      const formatterKey = formatter?.key;

      if (formatterKey) {
        // eslint-disable-next-line no-underscore-dangle
        const original = JSON.parse(entries[idx]._original);
        const formatType = get(original, formatterKey, '');

        switch (formatType.toLowerCase()) {
          case 'number':
          case 'weight':
            formatter = { type: 'number-like' };
            break;
          case 'percentage':
            formatter = { type: 'percentage' };
            break;
          case 'monetary':
            formatter = { type: 'currency' };
            break;
          default:
            formatter = undefined;
            break;
        }
      }
    }

    // Return the reference value (refValue) for max and min operations or return the formatted/mapped value
    return value.refValue
      ? formatValue(
          value.refValue,
          formatter,
          (value as AggregationMapValue).mapping,
        )
      : formatValue(
          intermediate,
          formatter,
          (value as AggregationMapValue).mapping,
        );
  }

  // Empty aggregation cells never became an aggregation object above (real
  // values — including a genuine 0 — take the isObject branch). When the column
  // configures a defaultValue, surface it (e.g. '-') instead of the 0
  // placeholder seeded in getInitialValue.
  if (defaultValueByKey[k] !== undefined) {
    return defaultValueByKey[k];
  }

  // This is the _original key or a string/number
  return value as string | number;
};

/**
 * Convert table object into array of objects
 */
const buildConvertObjectArray =
  (defaultValueByKey: Record<string, string | number>) =>
  (
    entry: IntermediaryMappedData,
    _idx: number,
    array: IntermediaryMappedData[],
  ): TableData => {
    const result: TableData = {};

    // Finalize aggregations and format data
    // eslint-disable-next-line @typescript-eslint/naming-convention
    const { _total, ...values } = entry;
    Object.entries(values).forEach(([k, value]) => {
      const key = sanitizeKey(k);
      result[key] = performSecondDataPass(
        k,
        value as IntermediaryMappedValue,
        array,
        _idx,
        defaultValueByKey,
      );
      if (entry.subRows) {
        result.subRows = Object.values(entry.subRows).map(
          buildConvertObjectArray(defaultValueByKey),
        );
      }
    });

    return result;
  };

/**
 * Change aggregation key property name on subrows
 * Rows and subrows are usually aggregated by different keys,
 * but the generated objects must have equal properties names to be rendered in the same columns
 */
const mergeRowsStructure = (
  aggrKey: string,
  subRowsAggrKey: string,
  tableData: TableData[],
) =>
  tableData.map((row) => {
    row.subRows = row.subRows?.map((subRow) => {
      subRow[sanitizeKey(aggrKey)] = subRow[sanitizeKey(subRowsAggrKey)];
      return subRow;
    });
    return row;
  }) || [];

/**
 * Maps the data into a defined aggregation object format
 * @param data
 * @param groupBy
 * @param aggregation
 * @returns
 */
export const mapData: DataParser = ({
  data,
  aggrKey,
  groupBy,
  reference,
  subRows,
}) => {
  // Build the table object. Only need to prebuild the object structure
  // if this table needs to perform aggregations
  const tableObject: Record<string, IntermediaryMappedData> = {};

  const buildTableObject = (
    tableObj: Record<string, IntermediaryMappedData>,
    rowAggrKey: AggrKeyInterface,
    rowGroupBy: GroupByInterface[],
    parentRow?: IntermediaryMappedData,
  ) => {
    // If this line doesn't have a parent then it is the parent
    const isParentRow = !parentRow;

    const buildInnerObject = getInnerObjectBuilder(
      rowGroupBy,
      reference,
      isParentRow,
    );

    if (Object.keys(reference).length !== 0 && rowAggrKey.reference) {
      reference[rowAggrKey.reference.id]?.forEach((ref) => {
        tableObj[ref.id] = buildInnerObject();
        // save the key inside the object so we don't need to re-check this
        tableObj[ref.id][rowAggrKey.key] = ref.label;
      });
    }

    // Iterate over data setting the data
    data.forEach((d) => {
      // Check the data for the related aggregation key
      let key = parseDataValue(
        d,
        rowAggrKey.key,
        rowAggrKey.dataType || 'string',
      ) as string | number;
      // If rowAggrKey has a referenceMap, match the reference row by `match` function
      if (Object.keys(reference).length !== 0 && rowAggrKey.reference) {
        const refMatch = reference[rowAggrKey.reference.id]?.find((ref) =>
          ref.match(key),
        );
        key = refMatch?.id || rowAggrKey.key;
      } else {
        // Apply the key formatter
        key = formatValue(key, rowAggrKey.formatter);
      }

      // If it's a subrow, ignore elements outside this group
      if (parentRow) {
        const rowParentKey = parseDataValue(
          d,
          aggrKey.key,
          aggrKey.dataType || 'string',
        );
        if (rowParentKey !== parentRow[aggrKey.key]) return;
      }

      // Initialize object if needed
      if (!tableObj[key]) {
        tableObj[key] = buildInnerObject();
      }

      // Keep original data for reference/debug
      // Note: The CustomCell uses this field for parsing additional values
      // eslint-disable-next-line no-underscore-dangle
      tableObj[key]._original = JSON.stringify(d);

      // Iterate over each group, setting the data
      rowGroupBy.forEach((g) => {
        // Parse the value for this group
        const parsedKeyValue = parseDataValue(
          d,
          g.key,
          g.dataType || 'string',
        ) as string | number;

        // Check references if defined
        let groupKey = g.key;
        if (reference && g.reference) {
          const refMatch = reference[g.reference.id]?.find((ref) =>
            ref.match(parsedKeyValue),
          );
          groupKey = refMatch?.label || g.key;
        }

        // Check if this group has subgroups. If it has, we need to iterate
        // over each subgroup
        const { subGroups } = g as TableGroupByInterface;
        const hasSubgroups = subGroups && subGroups?.length > 0;

        // Iterate over subgroups (or this group, so we can reuse code)
        (subGroups || [g]).forEach((sg) => {
          // Skip data if ignoreIfNull matches
          if (sg.ignoreIfNull) {
            const referenceKeyValue = parseDataValue(
              d,
              sg.ignoreIfNull.key,
              sg.ignoreIfNull.dataType || 'string',
            ) as string | number;
            if (referenceKeyValue === 'null') {
              return;
            }
          }

          // If this has subgroups, create the obj path
          const objPath = hasSubgroups
            ? `${groupKey}-${sanitizeKey(sg.key)}`
            : groupKey;

          // Hide this column on parent rows
          if (isParentRow && (sg as TableGroupByInterface).hideOnParentRow) {
            return;
          }

          // Skip aggregation if data is filtered-out by the sg.filters
          if (
            Array.isArray(sg.filters) &&
            sg.filters.length > 0 &&
            !isDataValid(d, sg.filters)
          ) {
            return;
          }

          // If field is an aggregation - perform the first-pass of the algorithm
          if (sg.aggregation) {
            const aggregator = firstPassAggregateFunction(sg.aggregation, d);
            const aggrValue = aggregator(
              (tableObj[key][objPath] || {}) as Partial<AggregateObj>,
            );
            tableObj[key][objPath] = aggrValue;

            // Add formatter and mapping to the object so we can format it later on
            (tableObj[key][objPath] as AggregationMapValue).formatter =
              sg.formatter;
            (tableObj[key][objPath] as AggregationMapValue).mapping =
              sg.mapping;

            return;
          }

          // If field is already set (and it is not an aggregation)
          // and group has reference, we can safely ignore
          if (rowAggrKey.reference && rowAggrKey.key === sg.key) {
            return;
          }

          // If field does not contain aggregation
          // Parse the value for this group
          const subgroupKeyValue = parseDataValue(
            d,
            sg.key,
            sg.dataType || 'string',
          ) as string | number;
          tableObj[key][objPath] = formatValue(
            subgroupKeyValue,
            sg.formatter,
            sg.mapping,
          );
        });
      });
    });
  };

  // Build main rows
  buildTableObject(tableObject, aggrKey, groupBy);

  // Build subrows
  if (subRows) {
    Object.keys(tableObject).forEach((row) => {
      tableObject[row].subRows = {};
      const rowGroupBy = cloneDeep(groupBy);
      rowGroupBy[0].key = subRows.aggrKey.key;

      buildTableObject(
        tableObject[row].subRows as unknown as Record<
          string,
          IntermediaryMappedData
        >,
        subRows.aggrKey,
        rowGroupBy,
        tableObject[row],
      );
    });
  }

  // Convert object to array of object performing the required aggregations.
  // Aggregation columns that declare a defaultValue surface it for their empty
  // cells (see buildDefaultValueMap) instead of the seeded 0.
  const convertObjectArray = buildConvertObjectArray(
    buildDefaultValueMap(groupBy, reference),
  );
  const tableData: ReturnType<DataParser> =
    Object.values(tableObject).map(convertObjectArray);

  // Change aggregation key property name on subrows
  if (subRows) {
    return mergeRowsStructure(aggrKey.key, subRows?.aggrKey.key, tableData);
  }

  return tableData;
};
